import {
  ArrowRight02Icon,
  BlushBrush02Icon,
  BookOpen02Icon,
  ComputerTerminal01Icon,
  ConnectIcon,
  Download02Icon,
  GlobalIcon,
  Home01Icon,
  Idea01Icon,
  MapsIcon,
  MessageMultiple02Icon,
  PackageOpenIcon,
  QuillWrite01Icon,
  UserGroupIcon,
} from "@icons";
import Image from "next/image";
import type { ReactElement } from "react";
import {
  DiscordIcon,
  Github,
  HeartHandIcon,
  LinkedinIcon,
  TwitterIcon,
  WhatsappIcon,
  YoutubeIcon,
} from "@/components/shared/icons";

const BOT_ICONS: { src: string; alt: string; rotate: string }[] = [
  {
    src: "/images/icons/macos/discord.webp",
    alt: "Discord",
    rotate: "-rotate-6",
  },
  { src: "/images/icons/macos/slack.webp", alt: "Slack", rotate: "rotate-6" },
  {
    src: "/images/icons/macos/telegram.webp",
    alt: "Telegram",
    rotate: "-rotate-6",
  },
  {
    src: "/images/icons/macos/whatsapp.webp",
    alt: "WhatsApp",
    rotate: "rotate-6",
  },
];

const BotStackIcon = (): ReactElement => (
  <div className="flex -space-x-3">
    {BOT_ICONS.map((bot) => (
      <div
        key={bot.alt}
        className={`relative size-9 shrink-0 ${bot.rotate} drop-shadow-md transition-transform duration-200 group-hover:rotate-0`}
      >
        <Image
          src={bot.src}
          alt={bot.alt}
          fill
          sizes="36px"
          className="object-contain"
        />
      </div>
    ))}
  </div>
);

export interface AppLink {
  label: string;
  /** Override the label when rendered in the footer. */
  footerLabel?: string;
  href: string;
  icon?: ReactElement;
  /** Render the icon without the default pill background (e.g. stacked logos). */
  richIcon?: boolean;
  external?: boolean;
  requiresAuth?: boolean;
  guestOnly?: boolean;
  commented?: boolean;
  description?: string;
  hideFooter?: boolean;
  hideNavbar?: boolean;
}

export interface LinkSection {
  title: string;
  links: AppLink[];
}

export const appConfig = {
  // Site information
  site: {
    name: "GAIA",
    copyright: "Copyright © 2025 The Experience Company. All rights reserved.",
    domain: "heygaia.io",
    mobileWaitlist:
      "https://heygaia.notion.site/307023640e7b802ca404dee12cb04e89?pvs=105",
  },

  // Core link definitions - single source of truth
  links: {
    // Primary navigation links (used in navbar)
    main: [
      {
        href: "/",
        label: "Home",
        icon: <Home01Icon width={20} height={20} color={"currentColor"} />,
        description: "Return to the home page",
      },
    ] as AppLink[],

    // Navigation menu sections
    product: [
      {
        href: "/use-cases",
        label: "Use Cases",
        icon: <Idea01Icon width={20} height={20} color={"currentColor"} />,
        description: "Here's what GAIA can do for you",
      },
      {
        href: "/marketplace",
        label: "Integration Marketplace",
        icon: <ConnectIcon width={20} height={20} color={"currentColor"} />,
        description: "Connect GAIA to the tools you already use",
      },
      {
        href: "/download",
        label: "Download",
        icon: <Download02Icon width={20} height={20} color={"currentColor"} />,
        description: "GAIA on your Mac, phone, and browser",
      },
      {
        href: "/cli",
        label: "Self-Host CLI",
        icon: (
          <ComputerTerminal01Icon
            width={20}
            height={20}
            color={"currentColor"}
          />
        ),
        description: "Keep your data on your own infrastructure",
      },
      {
        href: "/bots",
        label: "Talk to GAIA in the apps you already use",
        footerLabel: "Bots",
        icon: <BotStackIcon />,
        richIcon: true,
        description: "Without leaving Slack, Discord, Telegram, or WhatsApp",
      },
      {
        href: "/features",
        label: "Features",
        icon: <MapsIcon width={20} height={20} color={"currentColor"} />,
        description: "Every capability in one place",
      },
      {
        href: "/roadmap",
        label: "Roadmap",
        icon: <MapsIcon width={20} height={20} color={"currentColor"} />,
        external: true,
        description: "What we're shipping next",
      },
      {
        href: "/for",
        label: "Tailored For Your Role",
        icon: <UserGroupIcon width={20} height={20} color={"currentColor"} />,
        description: "Workflows tuned for your job, not generic ones",
        hideFooter: true,
      },
      {
        href: "/compare",
        label: "Compare",
        icon: <GlobalIcon width={20} height={20} color={"currentColor"} />,
        description: "Side-by-side with ChatGPT, Claude, and Poke",
      },
    ] as AppLink[],

    resources: [
      {
        href: "/blog",
        label: "Blog",
        icon: (
          <QuillWrite01Icon width={20} height={20} color={"currentColor"} />
        ),
        description: "Stories, lessons, and product updates",
      },
      {
        href: "https://docs.heygaia.io/release-notes",
        label: "Release Notes",
        icon: <PackageOpenIcon width={20} height={20} color={"currentColor"} />,
        external: true,
        description: "What's new, what's fixed, what's better",
      },
      {
        href: "https://docs.heygaia.io",
        label: "Documentation",
        icon: <BookOpen02Icon width={20} height={20} color={"currentColor"} />,
        external: true,
        description: "Setup, guides, and API reference",
      },
      {
        href: "/request-feature",
        label: "Request a Feature",
        icon: <Idea01Icon width={20} height={20} color={"currentColor"} />,
        external: true,
        description: "Tell us what to build next",
      },
      {
        href: "/status",
        label: "Status",
        icon: <GlobalIcon width={20} height={20} color={"currentColor"} />,
        external: true,
        description: "Live system health",
      },
      {
        href: "/learn",
        label: "Glossary",
        icon: <BookOpen02Icon width={20} height={20} color={"currentColor"} />,
        description: "AI and productivity terms explained",
        hideNavbar: true,
      },
      {
        href: "/alternative-to",
        label: "Alternatives",
        icon: (
          <ArrowRight02Icon width={20} height={20} color={"currentColor"} />
        ),
        description: "See which tools GAIA replaces and how it compares",
        hideNavbar: true,
      },
      {
        href: "/automate",
        label: "Automation Combos",
        icon: <ConnectIcon width={20} height={20} color={"currentColor"} />,
        description: "Recipes for connecting any two tools",
      },
      {
        href: "/feed.xml",
        label: "RSS Feed",
        icon: <GlobalIcon width={20} height={20} color={"currentColor"} />,
        description: "Subscribe to all GAIA pages and updates via RSS",
        hideNavbar: true,
      },
    ] as AppLink[],

    company: [
      {
        href: "/about",
        label: "About",
        icon: <GlobalIcon width={20} height={20} color={"currentColor"} />,
        description: "Learn about GAIA and the team behind it",
      },
      {
        href: "/manifesto",
        label: "Manifesto",
        icon: <GlobalIcon width={20} height={20} color={"currentColor"} />,
        description: "Our mission and vision",
      },
      {
        href: "/brand",
        label: "Branding",
        icon: (
          <BlushBrush02Icon width={20} height={20} color={"currentColor"} />
        ),
        description: "Brand guidelines and downloadable assets",
      },
      {
        href: "/thanks",
        label: "Tools We Love",
        icon: <HeartHandIcon width={20} height={20} color={"currentColor"} />,
        description: "Open source tools that power GAIA",
      },
      {
        href: "/contact",
        label: "Contact",
        icon: <HeartHandIcon width={20} height={20} color={"currentColor"} />,
        description: "Get in touch with our team",
      },
      {
        href: "/terms",
        label: "Terms",
        icon: <BookOpen02Icon width={20} height={20} color={"currentColor"} />,
        description: "Terms of service and usage",
        hideFooter: true,
      },
      {
        href: "/privacy",
        label: "Privacy",
        icon: <BookOpen02Icon width={20} height={20} color={"currentColor"} />,
        description: "Our privacy policy",
        hideFooter: true,
      },
    ] as AppLink[],

    connect: [
      {
        href: "https://discord.heygaia.io",
        label: "Discord",
        icon: <DiscordIcon width={20} height={20} color="#5865f2" />,
        external: true,
        description: "Join Discord Community",
      },
      {
        href: "https://x.com/trygaia",
        label: "Twitter",
        icon: <TwitterIcon width={20} height={20} color="#08a0e9" />,
        external: true,
        description: "Follow us for updates",
      },
      {
        href: "https://github.com/theexperiencecompany",
        label: "GitHub",
        icon: <Github width={20} height={20} color="white" />,
        external: true,
        description: "Check out our open source projects",
      },
      {
        href: "https://whatsapp.heygaia.io",
        label: "WhatsApp",
        icon: <WhatsappIcon width={20} height={20} color="#25D366" />,
        external: true,
        description: "Join WhatsApp Community",
      },
      {
        href: "https://youtube.com/@theexperiencecompany",
        label: "YouTube",
        icon: <YoutubeIcon width={25} height={25} color="#FF0000" />,
        external: true,
        description: "Subscribe to our YouTube Channel",
      },
      {
        href: "https://www.linkedin.com/company/heygaia",
        label: "LinkedIn",
        icon: <LinkedinIcon width={20} height={20} color="#0077B5" />,
        external: true,
        description: "Follow our LinkedIn Company Page",
      },
    ] as AppLink[],

    personas: [
      {
        href: "/for/startup-founders",
        label: "Startup Founders",
        description: "AI chief of staff for founders",
        hideNavbar: true,
      },
      {
        href: "/for/software-developers",
        label: "Software Developers",
        description: "Automate standups and GitHub workflows",
        hideNavbar: true,
      },
      {
        href: "/for/sales-professionals",
        label: "Sales Professionals",
        description: "AI CRM monitor and follow-up automation",
        hideNavbar: true,
      },
      {
        href: "/for/product-managers",
        label: "Product Managers",
        description: "Automate stakeholder updates and sprint reports",
        hideNavbar: true,
      },
      {
        href: "/for/engineering-managers",
        label: "Engineering Managers",
        description: "1:1 prep, sprint reports, and team analytics",
        hideNavbar: true,
      },
      {
        href: "/for/agency-owners",
        label: "Agency Owners",
        description: "Run 10 clients without losing your mind",
        hideNavbar: true,
      },
      {
        href: "/for",
        label: (
          <div className="flex items-center gap-1">
            <span>View All Roles</span>
            <ArrowRight02Icon width={15} height={15} />
          </div>
        ),
        description: "See all personas GAIA is built for",
        hideNavbar: true,
      },
    ] as AppLink[],

    // Authentication related links
    auth: [
      {
        href: "/login",
        label: "Login",
        guestOnly: true,
      },
      {
        href: "/signup",
        label: "Get Started",
        guestOnly: true,
      },
      {
        href: "/c",
        label: "Chat",
        icon: <MessageMultiple02Icon width={17} color={"currentColor"} />,
        requiresAuth: true,
      },
    ] as AppLink[],
  },

  // Footer mapping - references existing link categories
  footerMapping: {
    Product: ["product"],
    Resources: ["resources"],
    "Built For": ["personas"],
    Company: ["company"],
    Socials: ["connect"],
  } as Record<string, string[]>,
};

// Utility functions for footer generation
const getFooterSections = (): LinkSection[] => {
  return Object.entries(appConfig.footerMapping).map(([title, categories]) => ({
    title,
    links: categories.flatMap(
      (category) =>
        appConfig.links[category as keyof typeof appConfig.links] || [],
    ),
  }));
};

// Streamlined exports
export const footerSections = getFooterSections();

// Direct access to link categories for navigation
export const { product, resources, company, connect, auth } = appConfig.links;

// Utility function to get description for a link by label
export const getLinkDescription = (label: string): string => {
  const allLinks = Object.values(appConfig.links).flat();
  const link = allLinks.find((link) => link.label === label);
  return link?.description || "";
};

// Utility function to get a link by label from all categories
export const getLinkByLabel = (label: string): AppLink | undefined => {
  const allLinks = Object.values(appConfig.links).flat();
  return allLinks.find((link) => link.label === label);
};
